import { onMounted, reactive, ref, watch, nextTick } from "vue";
import api from "@/api/api";
import {toast} from '@/components/utils/ui_v4/alerts/toast/toast';

export const OBSERVACAO_LOCAL_RECEBIMENTO_MAX = 255;

export default function useLocalRecebimentoForm(props, emit) {
  const dataSet = ref([]);

  const emptyForm = () => ({
    filial: null,
    endereco: "",
    inicio: "",
    fim: "",
    dias: [],
    observacao: "",
  });

  const isPopulating = ref(false);

  const formData = reactive(emptyForm());

  const validate = reactive({
    filial: "",
    endereco: "",
    inicio: "",
    fim: "",
    dias: "",
    observacao: "",
  });

  onMounted(async () => {
    try {
      const { data } = await api.get(
        "/cotacao/criar_cotacao_listar_empresa_filial"
      );
      dataSet.value = data.empresas_filiais || [];

      // Se houver apenas uma empresa, selecionar automaticamente
      if (dataSet.value.length === 1 && !formData.filial) {
        formData.filial = dataSet.value[0].idfilial;
      }
    } catch (e) {
      toast.error('Erro ao listar empresas filiais!');
      
    }
  });

  const fillFormFromEdit = (value) => {
    if (!value) return;

    isPopulating.value = true;

    // normalizar dias (array ou "a,b")
    const dias = Array.isArray(value.dias)
      ? value.dias
      : typeof value.dias === "string" && value.dias.length
        ? value.dias.split(",").map((d) => d.trim()).filter(Boolean)
        : [];

    // normalizar filial (pode ser id, idfilial, ou objeto)
    let filialId = null;
    if (value.filial && typeof value.filial === "object") {
      filialId = value.filial.idfilial || value.filial.id || null;
    } else {
      filialId = value.filial || value.idfilial || value.id || null;
    }

    // preencher campos
    formData.filial = filialId;
    formData.endereco = value.endereco || value.titulo || "";
    formData.inicio = value.inicio || "";
    formData.fim = value.fim || "";
    formData.dias = dias;
    const obs = value.observacao || "";
    formData.observacao =
      typeof obs === "string" && obs.length > OBSERVACAO_LOCAL_RECEBIMENTO_MAX
        ? obs.slice(0, OBSERVACAO_LOCAL_RECEBIMENTO_MAX)
        : obs;

    // Preservar ID se existir (para edição)
    if (value.id) {
      formData.id = value.id;
    }
    if (value.cotacao_local_recebimento_id) {
      formData.cotacao_local_recebimento_id = value.cotacao_local_recebimento_id;
    }

    Object.keys(validate).forEach((k) => (validate[k] = ""));

    nextTick(() => {
      isPopulating.value = false;
    });
  };

  // Quando seleciona uma filial no select auto preencher endereço
  watch(
    () => formData.filial,
    (id) => {
      if (!id || isPopulating.value) return;
      const filial = dataSet.value.find((f) => f.idfilial == id || f.id == id);
      if (filial) {
        formData.endereco = filial.endereco || filial.logradouro || filial.rua || "";
      }
    }
  );

  // Se editData chegar ou mudar e o modal já estiver aberto preenche
  watch(
    () => props.editData,
    (value) => {
      if (value && props.visible) {
        // post para garantir que o DOM / v-select já está pronto
        nextTick(() => fillFormFromEdit(value));
      }
    },
    { deep: true }
  );

  // Se o modal abrir e já existir editData preenche (cobre a outra ordem)
  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        if (props.editData) {
          // post para evitar conflitos com foco do dialog
          nextTick(() => fillFormFromEdit(props.editData));
        } else {
          // Se não há dados de edição e há apenas uma empresa, selecionar automaticamente
          if (dataSet.value.length === 1 && !formData.filial) {
            formData.filial = dataSet.value[0].idfilial;
          }
        }
      } else {
        // fechar reset seguro
        const clean = emptyForm();
        Object.keys(formData).forEach(key => {
          if (key in clean) {
            formData[key] = clean[key];
          } else {
            delete formData[key]; // Remove propriedades extras como id
          }
        });
        Object.keys(validate).forEach((k) => (validate[k] = ""));
      }
    },
    { flush: "post" } // importante para evitar race conditions com focus-trap
  );

  // validação
  function parseTimeHHmm(hhmm) {
    if (!hhmm || typeof hhmm !== "string" || hhmm.length < 4) return null;
    const [hhStr, mmStr] = hhmm.split(":");
    const hh = parseInt(hhStr, 10);
    const mm = parseInt(mmStr, 10);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return { hh, mm };
  }

  function toMinutes(t) {
    return t.hh * 60 + t.mm;
  }

  const validateForm = () => {
    let valid = true;

    validate.filial = formData.filial ? "" : "Selecione uma filial";
    validate.endereco = formData.endereco ? "" : "Informe o endereço";
    validate.inicio = formData.inicio ? "" : "Informe a hora inicial";
    validate.fim = formData.fim ? "" : "Informe a hora final";

    // Validar horários quando preenchidos
    let tIni = null;
    let tFim = null;
    if (formData.inicio) {
      tIni = parseTimeHHmm(formData.inicio);
      if (!tIni) {
        validate.inicio = "Hora inicial inválida";
        valid = false;
      }
    }
    if (formData.fim) {
      tFim = parseTimeHHmm(formData.fim);
      if (!tFim) {
        validate.fim = "Hora final inválida";
        valid = false;
      }
    }

    // Ordem lógica: fim deve ser maior que início
    if (tIni && tFim) {
      if (toMinutes(tFim) <= toMinutes(tIni)) {
        validate.fim = "Hora final deve ser maior que a inicial";
        valid = false;
      }
    }

    if (!formData.dias || !formData.dias.length) {
      validate.dias = "Selecione ao menos um dia";
      valid = false;
    } else {
      validate.dias = "";
    }

    const obsLen = (formData.observacao || "").length;
    if (obsLen > OBSERVACAO_LOCAL_RECEBIMENTO_MAX) {
      validate.observacao = `Observações: no máximo ${OBSERVACAO_LOCAL_RECEBIMENTO_MAX} caracteres (${obsLen} informados).`;
      valid = false;
    } else {
      validate.observacao = "";
    }

    if (!formData.filial || !formData.endereco || !formData.inicio || !formData.fim) {
      valid = false;
    }

    return valid;
  };

  const onSaveClick = () => {
    if (!validateForm()) return;

    emit("save", Object.assign({}, formData));
  };

  return {
    dataSet,
    formData,
    validate,
    onSaveClick,
    fillFormFromEdit,
    observacaoMaxLen: OBSERVACAO_LOCAL_RECEBIMENTO_MAX,
  };
}
